/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import CustomGrid from 'components/grids/CustomGrid';
import { SubTitleLayout, SubTitleGroup, GlobalBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { SetupProjectManager } from 'models/setup/SetupProjectManager';
import { findProject } from 'apis/setup/SetupProject';
import { useLoading } from 'components/process/Loading';
import { findPrjExcpColorList, savePrjExcpColor } from 'apis/admin/ProjectExcpColor';
import * as input from '@grapecity/wijmo.input';
import { ProjectExcp } from 'models/admin/ExcpPartner';
import _ from 'lodash';

type Props = {
  open: boolean;
  close: () => void;
  condition: {
    elmPrjId: string;
    excpId: string;
  };
};

interface ConditionProps {
  elmPrjId: string;
}

const SetupExcpColorPopup = ({ open, close, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const excpId = props.condition.excpId;
  const initCondition = { elmPrjId: props.condition.elmPrjId };

  const gridRef = useRef<any>();
  const [prjMasterData, setPrjMasterData] = useState<SetupProjectManager[]>([]);
  const [condition, setCondition] = useState<ConditionProps>({ ...initCondition });
  const [rowData, setRowData] = useState<ProjectExcp[]>([]);

  const layoutDefinition = useMemo(() => {
    const styled = (value) => `
      background:${value};
      display: inline-block;
      width: 12px;
      height: 1.5em;
      margin: 0 3px 0 -3px;
      border: 1px solid black;
      vertical-align: bottom;`;

    return [
      {
        binding: 'excpNm',
        header: String(t('com.label.협력업체명', '협력업체명')),
        minWidth: 70,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'dispCorCd',
        header: String(t('setup.label.색상', '색상')),
        minWidth: 80,
        width: 200,
        align: 'left',
        cssClass: 'WijmoColor',
        cellTemplate: ({ value }) => `<span style="${styled(value)}"></span> ${value || 'None'}`,
        editor: new input.InputColor(document.createElement('div')),
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const onBeginningEdit = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;

    // 수정 불가능 한 cell 처리
    if (binding === 'dispCorCd') {
      if (!_.isEmpty(excpId) && item.excpId !== excpId) e.cancel = true;
    }
  };

  const initCodes = async () => {
    findProject({ searchVisibleSaveYn: 'N' }).then((result: SetupProjectManager[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnSearch = () => {
    // search
    openLoading(true);
    findPrjExcpColorList(condition)
      .then((res) => {
        setRowData(res.map((o) => ({ ...o })));
      })
      .finally(() => openLoading(false));
  };

  const btnSave = () => {
    // save
    openLoading(true);
    const data = rowData
      .filter((o) => o.crudKey === 'U')
      .map((o) => ({ excpId: o.excpId, elmPrjId: o.elmPrjId, dispCorCd: o.dispCorCd }));
    savePrjExcpColor(data)
      .then((res) => {
        if (res.data > 0) {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
          });
          close();
        } else {
          openMessageBar({
            type: 'error',
            content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <CustomDialog
      title={String(t('setup.label.협력업체 색상 변경', '협력업체 색상 변경'))}
      size={'md'}
      open={open}
      onClose={close}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="none">
              <SearchCols className="none">
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <label>
                  {prjMasterData.find((o) => o.elmPrjId === condition.elmPrjId)?.elmPrjNm}
                </label>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.협력업체 목록', '협력업체 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`setupPrjExcpColorGridId`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        height={400}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
      <GlobalBtnGroup>
        <Button css={IconButton.button} className="save" onClick={btnSave} disableRipple>
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>
    </CustomDialog>
  );
};

export default SetupExcpColorPopup;
